import { useCallback, useContext } from 'react';
import { useRecoilState } from 'recoil';
import { mutate as globalMutate } from 'swr';
import { unstable_serialize } from 'swr/infinite';

import { ChainlitContext } from '@chainlit/react-client';

import { getDeepIntelligenceThreadsKey } from '@/hooks/DMuseDeepIntelligenceThreads';
import { deepIntelligenceUnreadCountState } from '@/state/chat';

function revalidateThreadsList() {
    return globalMutate(unstable_serialize(getDeepIntelligenceThreadsKey));
}

export function useDeepIntelligenceUnreadCount() {
    const apiClient = useContext(ChainlitContext);
    const [unreadCount, setUnreadCount] = useRecoilState(
        deepIntelligenceUnreadCountState
    );

    const refresh = useCallback(() => {
        return apiClient
            .get('/project/deep-intelligence/notifications/unread-count')
            .then((res) => res.json())
            .then((data) => setUnreadCount(data?.count || 0))
            .catch(() => {});
    }, [apiClient, setUnreadCount]);

    const markAllRead = useCallback(() => {
        setUnreadCount(0);
        return apiClient
            .post('/project/deep-intelligence/notifications/mark-all-read', {})
            .then(() => revalidateThreadsList())
            .catch(() => refresh());
    }, [apiClient, setUnreadCount, refresh]);

    const markOneRead = useCallback(
        (threadId: string) => {
            return apiClient
                .post(`/project/deep-intelligence/notifications/${threadId}/read`, {})
                .then(() => Promise.all([refresh(), revalidateThreadsList()]))
                .catch(() => {});
        },
        [apiClient, refresh]
    );

    return { unreadCount, refresh, markAllRead, markOneRead };
}
